import { useState, useEffect } from 'react';
import { TrendingUp, Users, DollarSign, Target, AlertCircle } from 'lucide-react';
import { AdminLayout } from '../../components/admin/layout/AdminLayout';
import { StatCard } from '../../components/admin/ui';
import { CRMAnalytics, getCRMAnalytics, PipelineStats, getPipelineStats, SPT_CONFIG } from '../../utils/crmScoringService';

export function CRMAnalysePage() {
  const [analytics, setAnalytics] = useState<CRMAnalytics | null>(null);
  const [pipelineStats, setPipelineStats] = useState<PipelineStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [analyticsData, pipelineData] = await Promise.all([
        getCRMAnalytics(),
        getPipelineStats(),
      ]);
      setAnalytics(analyticsData);
      setPipelineStats(pipelineData);
    } catch (error) {
      console.error('Error loading CRM analytics:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading || !analytics) {
    return (
      <AdminLayout title="Analyse CRM" description="Tableaux de bord et KPIs">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Analyse CRM" description="Vue d'ensemble de votre pipeline commercial">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Prospects"
            value={analytics.total_prospects.toString()}
            icon={Users}
            trend={{ value: 0, isPositive: true }}
          />
          <StatCard
            title="Pipeline Actif"
            value={analytics.active_pipeline.toString()}
            icon={Target}
            trend={{ value: 0, isPositive: true }}
          />
          <StatCard
            title="MRR Potentiel"
            value={`${Math.round(analytics.total_potential_mrr).toLocaleString('fr-FR')} €`}
            icon={DollarSign}
            trend={{ value: 0, isPositive: true }}
          />
          <StatCard
            title="MRR Pondéré"
            value={`${Math.round(analytics.weighted_mrr).toLocaleString('fr-FR')} €`}
            icon={TrendingUp}
            trend={{ value: 0, isPositive: true }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par SPT</h3>
            <div className="space-y-4">
              {[4, 3, 2, 1].map(spt => {
                const config = SPT_CONFIG[spt as keyof typeof SPT_CONFIG];
                const count = spt === 4 ? analytics.spt_critical :
                             spt === 3 ? analytics.spt_high :
                             spt === 2 ? analytics.spt_medium :
                             analytics.spt_low;
                const percentage = analytics.total_prospects > 0
                  ? Math.round((count / analytics.total_prospects) * 100)
                  : 0;

                const colorClass = config.color === 'red' ? 'bg-red-500' :
                                 config.color === 'orange' ? 'bg-orange-500' :
                                 config.color === 'yellow' ? 'bg-yellow-500' :
                                 'bg-blue-500';

                return (
                  <div key={spt}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        SPT {spt} - {config.label}
                      </span>
                      <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${colorClass}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Indicateurs Clés</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Stress Moyen Pipeline</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.avg_stress_level}</p>
                </div>
                <AlertCircle className="text-orange-500" size={32} />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Prob. Conversion Moyenne</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(analytics.avg_conversion_probability * 100)}%
                  </p>
                </div>
                <TrendingUp className="text-green-500" size={32} />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">MRR Moyen par Prospect</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(analytics.total_potential_mrr / Math.max(analytics.active_pipeline, 1)).toLocaleString('fr-FR')} €
                  </p>
                </div>
                <DollarSign className="text-blue-500" size={32} />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline par Statut</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Statut</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Prospects</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">MRR Total</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">MRR Pondéré</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">SPT Moyen</th>
                </tr>
              </thead>
              <tbody>
                {pipelineStats.map(stat => (
                  <tr key={stat.status} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm text-gray-900">{stat.status}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{stat.count}</td>
                    <td className="py-3 px-4 text-sm font-semibold text-green-700">
                      {Math.round(stat.total_mrr).toLocaleString('fr-FR')} €
                    </td>
                    <td className="py-3 px-4 text-sm font-semibold text-blue-700">
                      {Math.round(stat.weighted_mrr).toLocaleString('fr-FR')} €
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900">{stat.avg_spt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
